import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '../../store/userStore';
import AuthService from '../../API/AuthService';
import cl from './Cabinet.module.css';
import {
    IconArrowRight, IconExternal, IconGamepad, IconRobot,
    IconDashboard, IconCheck, IconStar, IconMail,
} from '../../components/icons';
import InvoiceModal from './InvoiceModal';
import CallbackModal from './CallbackModal';
import PayModal from './PayModal';
import DetailsModal from './DetailsModal';
import HistoryBlock from './HistoryBlock';
import usePageMeta from '../../hooks/usePageMeta';
import Spinner from '../../components/Spinner';

// Кабинет руководителя центра.
// 3 состояния:
//   - lead    — нет оплаты, активен демо-режим
//   - active  — есть подписка, выводим срок и лимиты
//   - expired — подписка истекла, нужно продлить

export default function Cabinet() {
    usePageMeta({ title: 'Кабинет · СтартКласс' });
    const navigate = useNavigate();
    const { profile, centre, loading, refresh, logout, setCentre } = useUserStore();
    const [showInvoice, setShowInvoice]   = useState(false);
    const [showCallback, setShowCallback] = useState(false);
    const [showPay, setShowPay]           = useState(false);
    const [showDetails, setShowDetails]   = useState(false);

    useEffect(() => {
        if (!AuthService.isAuthenticated()) {
            navigate('/login', { replace: true });
            return;
        }
        refresh();
    }, [navigate, refresh]);

    if (loading) {
        return (
            <main className={cl.page}>
                <Spinner size="large" text="Загружаем кабинет" fullPage center/>
            </main>
        );
    }

    function onLogout() {
        logout();
        navigate('/');
    }

    const status = centre?.status || 'lead';
    const studentsLimit = centre?.students_limit || 200;
    const teachersLimit = centre?.teachers_limit || 5;
    // Сколько мест занято. null — «не знаем» (панель недоступна): тогда
    // показываем один лимит, а не «0 из 200», что читалось бы как «никого нет».
    const studentsUsed = centre?.students_used;
    const teachersUsed = centre?.teachers_used;

    return (
        <main className={cl.page}>
            <div className={cl.head}>
                <div>
                    <h1 className={cl.greeting}>
                        Здравствуйте{(profile?.nickname || profile?.firstName) ? `, ${profile.nickname || profile.firstName}` : ''}!
                    </h1>
                    <p className={cl.subline}>
                        {centre?.name
                            ? <>Кабинет центра <b>«{centre.name}»</b>{centre?.city ? <>, {centre.city}</> : null}.</>
                            : 'Кабинет руководителя центра СтартКласс.'
                        }
                    </p>
                </div>
                <div className={cl.headActions}>
                    {/* Раздел администратора платформы. Владельцам центров не
                        показываем — бэкенд им всё равно отдаст 403. */}
                    {profile?.role === 'admin' && (
                        <button className={cl.adminBtn} onClick={() => navigate('/admin')}>
                            Администрирование
                        </button>
                    )}
                    <button className={cl.logout} onClick={onLogout}>Выйти</button>
                </div>
            </div>

            <StatusBlock
                status={status} centre={centre}
                onPayCard={() => setShowPay(true)}
                studentsLimit={studentsLimit} teachersLimit={teachersLimit}
                studentsUsed={studentsUsed} teachersUsed={teachersUsed}
                onPay={() => setShowInvoice(true)}
                onCallback={() => setShowCallback(true)}
            />

            <PlatformsGrid status={status}/>

            <DetailsBlock centre={centre} email={profile?.email}
                onEdit={() => setShowDetails(true)}/>

            <HistoryBlock/>

            {showDetails && (
                <DetailsModal
                    centre={centre}
                    onClose={() => setShowDetails(false)}
                    // PUT возвращает карточку без счётчиков занятых мест —
                    // их считает только GET. Без переноса «42 / 200» после
                    // сохранения реквизитов схлопнулось бы обратно в «200».
                    onSaved={(saved) => saved && setCentre({
                        students_used: centre?.students_used,
                        teachers_used: centre?.teachers_used,
                        ...saved,
                    })}
                />
            )}
            {showPay && <PayModal onClose={() => setShowPay(false)}/>}
            {showInvoice && (
                <InvoiceModal
                    onClose={() => setShowInvoice(false)}
                    defaultEmail={centre?.email || profile?.email}
                    defaultPhone={centre?.phone}
                />
            )}
            {showCallback && (
                <CallbackModal
                    onClose={() => setShowCallback(false)}
                    defaultEmail={centre?.email || profile?.email}
                    defaultPhone={centre?.phone}
                    defaultName={centre?.contact_person || profile?.firstName}
                />
            )}
        </main>
    );
}


// «5 дней», «2 дня», «1 день»
function plural(n, one, few, many) {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return many;
    if (b > 1 && b < 5) return few;
    if (b === 1) return one;
    return many;
}


function StatusBlock({ status, centre, studentsLimit, teachersLimit,
                       studentsUsed, teachersUsed, onPay, onPayCard, onCallback }) {
    // Сколько дней осталось до конца оплаченного срока. null — срока нет
    // (лид) или он уже прошёл.
    const daysLeft = centre?.expires_at
        ? Math.max(0, Math.ceil((Number(centre.expires_at) - Date.now()) / 86400000))
        : null;
    const soon = status === 'active' && daysLeft !== null && daysLeft <= 14;

    let badgeCls = cl.lead, label = 'Демо-период', title = 'Демо-доступ открыт',
        text = 'Вы можете посмотреть все три платформы и оценить, как они работают. Когда будете готовы — оплатите тариф и подключите свой первый поток учеников.',
        grad = 'linear-gradient(90deg, #3b82f6, #a855f7)';

    if (status === 'active') {
        badgeCls = cl.active; label = 'Активная подписка';
        title = 'Подписка активна';
        text = `Полный пакет открыт: ${studentsLimit} учеников и ${teachersLimit} преподавателей. Можно подключать аккаунты в любой момент.`;
        grad = 'linear-gradient(90deg, #10b981, #059669)';
    } else if (status === 'expired') {
        badgeCls = cl.expired; label = 'Подписка истекла';
        title = 'Доступ заморожен';
        text = 'Срок подписки истёк. Продлите тариф, чтобы вернуть доступ ученикам и преподавателям.';
        grad = 'linear-gradient(90deg, #ef4444, #dc2626)';
    }

    return (
        <div className={cl.statusCard} style={{ '--statusGrad': grad }}>
            <div>
                <span className={`${cl.statusBadge} ${badgeCls}`}>
                    <span className={cl.dot}/>
                    {label}
                </span>
                <h2 className={cl.statusTitle}>{title}</h2>
                <p className={cl.statusText}>{text}</p>

                {/* До какого числа оплачено — то, ради чего в кабинет и
                    заходят чаще всего. Раньше срок нигде не показывался. */}
                {centre?.expires_at && status !== 'lead' && (
                    <p className={cl.statusUntil}>
                        {status === 'expired' ? 'Срок закончился ' : 'Оплачено до '}
                        <b>{new Date(Number(centre.expires_at)).toLocaleDateString('ru-RU',
                            { day: 'numeric', month: 'long', year: 'numeric' })}</b>
                    </p>
                )}

                {/* Заметное предупреждение за две недели до конца.
                    Писем мы не шлём, поэтому кабинет — единственное место,
                    где владелец может об этом узнать: строчкой мелким шрифтом
                    тут не обойтись. */}
                {status === 'active' && daysLeft !== null && daysLeft <= 14 && (
                    <div className={cl.warnBox}>
                        <b>
                            {daysLeft === 0
                                ? 'Доступ заканчивается сегодня'
                                : `До конца доступа ${daysLeft} ${plural(daysLeft, 'день', 'дня', 'дней')}`}
                        </b>
                        <span>
                            Продлите заранее — оплата считается от текущей даты
                            окончания, оставшиеся дни не сгорают. Когда срок
                            выйдет, ученики потеряют доступ к платформам.
                        </span>
                    </div>
                )}

                <div className={cl.statusActions}>
                    {/* Продлевать можно и при действующей подписке: увидев
                        «осталось 3 дня», владелец раньше не мог ничего нажать —
                        кнопки показывались только лиду и после истечения. */}
                    {(status === 'lead' || status === 'expired' || soon) && (
                        <>
                            {/* Картой — доступ открывается сразу по вебхуку банка.
                                Счёт — когда бухгалтерии нужен банковский перевод. */}
                            <button className={cl.btnPrimary} onClick={onPayCard}>
                                <IconStar width={15} height={15}/>
                                {status === 'lead' ? 'Оплатить картой' : 'Продлить — оплатить картой'}
                            </button>
                            <button className={cl.btnGhost} onClick={onPay}>
                                Заказать счёт
                            </button>
                        </>
                    )}
                    <button className={cl.btnGhost} onClick={onCallback}>
                        <IconMail width={15} height={15}/>
                        Заказать звонок
                    </button>
                </div>
            </div>

            <div className={cl.limits}>
                <div className={cl.limit}>
                    <div className={cl.limitNum}>
                        {Number.isFinite(studentsUsed)
                            ? <>{studentsUsed}<span className={cl.limitOf}> / {studentsLimit}</span></>
                            : studentsLimit}
                    </div>
                    <div className={cl.limitLabel}>
                        {Number.isFinite(studentsUsed) ? 'Учеников подключено' : 'Учеников'}
                    </div>
                </div>
                <div className={cl.limit}>
                    <div className={cl.limitNum}>
                        {Number.isFinite(teachersUsed)
                            ? <>{teachersUsed}<span className={cl.limitOf}> / {teachersLimit}</span></>
                            : teachersLimit}
                    </div>
                    <div className={cl.limitLabel}>
                        {Number.isFinite(teachersUsed) ? 'Преподавателей добавлено' : 'Преподавателей'}
                    </div>
                </div>
                {/* Занимаются ли ученики на самом деле. Считается по
                    Майнкрафтии 2.0 — так и подписано: большинство пока учится
                    на старой платформе, и «2 из 117» без пояснения читалось бы
                    как «центр мёртвый». */}
                {centre?.activity ? (
                    <div className={cl.limit}>
                        <div className={cl.limitNum}>
                            {centre.activity.active}
                            <span className={cl.limitOf}> / {studentsUsed ?? '—'}</span>
                        </div>
                        <div className={cl.limitLabel}>
                            Занимаются в Майнкрафтии 2.0
                        </div>
                    </div>
                ) : (
                    <div className={cl.limit}>
                        <div className={cl.limitNum}>20+</div>
                        <div className={cl.limitLabel}>Курсов</div>
                    </div>
                )}
                <div className={cl.limit}>
                    <div className={cl.limitNum}>700+</div>
                    <div className={cl.limitLabel}>Уроков</div>
                </div>
            </div>
        </div>
    );
}


function PlatformsGrid({ status }) {
    // В демо у владельца центра единый JWT — он автоматически попадает
    // на платформы как залогиненный пользователь. После оплаты роли
    // переключим на teacher (для админки центра) — но это потом.
    // Робототехника больше не отдельный сайт: направление целиком
    // переехало в Майнкрафтию, ходить по нескольким адресам не нужно.
    const platforms = [
        {
            Icon: IconGamepad,
            title: 'Майнкрафтия',
            text: 'Программирование, математика и робототехника: Scratch, Python, '
                + 'электроника, Arduino, 3D-моделирование, Minecraft-серверы.',
            url: 'https://minecraftia-school.ru/',
            accent: '#3b82f6', soft: 'rgba(59, 130, 246, 0.1)',
        },
        {
            Icon: IconDashboard,
            title: 'Админ-панель центра',
            text: status === 'active'
                ? 'Ученики, журнал посещений, оплаты и проверка проектов.'
                : 'Демо-режим: посмотрите журнал и курсы до оплаты.',
            url: 'https://adminpanelminecraftia.ru/',
            accent: '#10b981', soft: 'rgba(16, 185, 129, 0.1)',
        },
        {
            Icon: IconRobot,
            title: 'Рублокс',
            text: '3D-редактор игр и собственный игровой мир: ученики создают '
                + 'и публикуют свои проекты.',
            url: 'https://rublox.pro/',
            accent: '#a855f7', soft: 'rgba(168, 85, 247, 0.1)',
        },
        {
            Icon: IconMail,
            title: 'Маська',
            text: 'Безопасный мессенджер для учеников центра: чаты, звонки, '
                + 'общение по учёбе.',
            url: 'https://маська.рф/',
            accent: '#f59e0b', soft: 'rgba(245, 158, 11, 0.1)',
        },
    ];

    return (
        <div className={cl.grid}>
            {platforms.map(p => {
                const Icon = p.Icon;
                return (
                    <a key={p.title} href={p.url}
                        target="_blank" rel="noopener noreferrer"
                        className={cl.platform}
                        style={{ '--accent': p.accent, '--accentSoft': p.soft }}>
                        <div className={cl.platformIcon}>
                            <Icon width={22} height={22}/>
                        </div>
                        <h3 className={cl.platformTitle}>{p.title}</h3>
                        <p className={cl.platformText}>{p.text}</p>
                        <span className={cl.platformLink}>
                            Открыть <IconExternal width={13} height={13}/>
                        </span>
                    </a>
                );
            })}
        </div>
    );
}


function DetailsBlock({ centre, email, onEdit }) {
    const fields = [
        { label: 'Название центра', value: centre?.name },
        { label: 'Город',            value: centre?.city },
        { label: 'Контактное лицо',  value: centre?.contact_person },
        { label: 'E-mail',           value: centre?.email || email },
        { label: 'Телефон',          value: centre?.phone },
        { label: 'Форма',            value: centre?.type === 'OOO' ? 'ООО' : 'ИП' },
        { label: 'ИНН',              value: centre?.inn },
        { label: 'Юр. адрес',        value: centre?.legal_address },
    ];

    return (
        <div className={cl.detailsCard}>
            <div className={cl.detailsHead}>
                <h3 className={cl.detailsTitle}>Реквизиты центра</h3>
                <button className={cl.editBtn} onClick={onEdit}>
                    Редактировать
                </button>
            </div>
            <div className={cl.detailsList}>
                {fields.map(f => (
                    <div key={f.label} className={cl.detail}>
                        <span className={cl.detailLabel}>{f.label}</span>
                        <span className={`${cl.detailValue} ${!f.value ? cl.empty : ''}`}>
                            {f.value || '— не указано'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
